import React, {useRef, useState} from 'react';
import CustomerActivity, {displayDate} from './CustomerActivity';
import {SellerMessageThread} from '../Messages/SellerMessages';

const tabs = ['Activity', 'Orders', 'Subscriptions & Access', 'Reviews', 'Private Files', 'Messages'];
const classicCustomer = id => `/account/subscribers/${id}?uiVersion=1`;
const reasonText = reason => reason ? reason.replace(/-/g, ' ') : 'Unavailable';

function Capabilities({customer}) {
  const classic = classicCustomer(customer.id);
  const actions = [
    ['sendMessage', 'Message', `${classic}#send_message`],
    ['sendThankYou', 'Thank You'],
    ['sharePrivateFile', 'Private File'],
    ['requestReview', 'Request Review', '/account/reviews?uiVersion=1'],
    ['reviewCustomer', 'Review Customer', `${classic}#review`],
    ['extendSubscription', 'Extend subscription', `${classic}#subscriptions`],
    ['cancelSubscription', 'Cancel subscription', `${classic}#subscriptions`],
    ['blockContact', 'Block Contact'],
    ['rescindBlock', 'Rescind Block'],
    ['rescindPrivateShare', 'Rescind Private Share'],
  ];
  return <div className="cd-actions" aria-label="Customer actions">
    {actions.map(([key, label, href]) => {
      const capability = customer.capabilities?.[key];
      const descriptionId = `cd-action-${key}`;
      return <div key={key}>
        {capability?.allowed && href
          ? <a href={href} aria-describedby={descriptionId}>{label}</a>
          : <button type="button" disabled aria-describedby={descriptionId}>{label}</button>}
        <small id={descriptionId}>{!capability?.allowed ? reasonText(capability?.reason)
          : href ? 'Opens classic tools' : 'Unavailable in this read-only beta'}</small>
      </div>;
    })}
  </div>;
}

function OrderList({orders, subscriptions = false}) {
  if (!orders?.length) return <p>{subscriptions ? 'No subscription access on this page.' : 'No orders yet.'}</p>;
  return <ul className="cd-records">{orders.map(order => <li key={order.uuid}>
    <a href={`/account/orders/${encodeURIComponent(order.uuid)}?uiVersion=1`}>
      {order.productName || 'Order'}
    </a>
    <p>{order.productType} · {order.status} · {order.isRefunded ? 'Refunded'
      : order.isCanceled ? 'Canceled' : order.isPurchased ? 'Purchased' : 'Unpaid'}</p>
    <p>Ordered: {displayDate(order.createdAt)}</p>
    {subscriptions && <p>Access until: {displayDate(order.validUntil)}</p>}
    <p>{order.totalAmount == null ? 'Amount unavailable'
      : `${(order.totalAmount / 100).toFixed(2)} ${order.currency || ''}`}</p>
  </li>)}</ul>;
}

function Reviews({reviews}) {
  return <div>{[['sellerAuthored', 'Reviews of customer'], ['customerAuthored', 'Reviews from customer']]
    .map(([key, title]) => <div key={key}>
      <h4>{title}</h4>
      {!reviews?.[key]?.length && <p>No {title.toLowerCase()} on this page.</p>}
      <ul className="cd-records">{reviews?.[key]?.map(review => <li key={review.uuid}>
        <p>Rating: {review.rating} · {displayDate(review.createdAt)}</p>
        <p>{review.comment}</p>
        {review.response && <p>Seller response: {review.response}</p>}
      </li>)}</ul>
    </div>)}</div>;
}

export default function CustomerDetail({customer, headingRef}) {
  const [tab, setTab] = useState('Activity');
  const tabRefs = useRef([]);
  const classic = classicCustomer(customer.id);
  const showTruncation = (tab === 'Orders' || tab === 'Subscriptions & Access')
    ? customer.pagination?.ordersHasMore
    : tab === 'Reviews' ? customer.pagination?.reviewsHasMore
      : tab === 'Private Files' ? customer.pagination?.privateSharesHasMore : false;
  return <div>
    <h2 ref={headingRef} tabIndex={-1}>{customer.name || 'Customer'}</h2>
    {customer.isAnonymized && <p>Customer identity hidden</p>}
    <p>{customer.email || 'No visible email'} · {customer.phone || 'No visible phone'}</p>
    <p>Customer since: {displayDate(customer.createdAt)}</p>
    <p>{customer.status?.blocked ? 'Contact blocked'
      : customer.status?.contactable ? 'Email contact available' : 'Email contact unavailable'}</p>
    {customer.ageBand && <p>Age band: {customer.ageBand}</p>}
    <p>Your customer rating: {customer.rating?.sellerAuthored ?? 'Not rated'}</p>
    {customer.contactBlock?.blockedAt && <p>Blocked: {displayDate(customer.contactBlock.blockedAt)}</p>}
    {customer.contactBlock?.reason && <p>Block reason: {customer.contactBlock.reason}</p>}
    <Capabilities customer={customer} />
    <div role="tablist" aria-label="Customer history" className="cd-tabs">
      {tabs.map((label, index) => <button key={label} type="button" role="tab"
        ref={node => { tabRefs.current[index] = node; }}
        id={`cd-tab-${index}`} aria-controls={`cd-panel-${index}`}
        aria-selected={tab === label} tabIndex={tab === label ? 0 : -1}
        onClick={() => setTab(label)} onKeyDown={event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            setTab(tabs[next]);
            tabRefs.current[next]?.focus();
          }
        }}>{label}</button>)}
    </div>
    {tabs.map((label, index) => <div key={label} role="tabpanel" id={`cd-panel-${index}`}
      aria-labelledby={`cd-tab-${index}`} hidden={tab !== label} tabIndex={0}>
      {label === 'Activity' && <CustomerActivity customerId={customer.id} />}
      {label === 'Orders' && <OrderList orders={customer.orders} />}
      {label === 'Subscriptions & Access' && <>
        <p>Subscription and upgrade orders shown here retain their server-reported access dates.</p>
        <OrderList orders={customer.subscriptions} subscriptions />
      </>}
      {label === 'Reviews' && <Reviews reviews={customer.reviews} />}
      {label === 'Private Files' && <>
        {!customer.privateShares?.length && <p>No private file shares on this page.</p>}
        <ul className="cd-records">{customer.privateShares?.map(share => <li key={share.tokenUuid}>
          <p>{share.filename || 'Private file'} · {share.status === 'revoked' ? 'Rescinded'
            : share.status === 'expired' ? 'Expired' : 'Active'}</p>
          <p>Shared: {displayDate(share.createdAt)} · Expires: {displayDate(share.expiresAt)}</p>
          {share.revokedAt && <p>Rescinded: {displayDate(share.revokedAt)}</p>}
        </li>)}</ul>
        <p>Rescinding a share cannot retract a completed or already in-flight download.</p>
      </>}
      {label === 'Messages' && tab === 'Messages' && (
        <SellerMessageThread key={customer.id} customerId={customer.id} readOnly />
      )}
    </div>)}
    {showTruncation && <p role="status">This panel shows a limited page of history. Open classic
      customers, orders, or reviews for more history.</p>}
    <p><a href={classic}>Open classic customer tools</a> ·{' '}
      <a href={`/account/messages/${customer.id}?uiVersion=1`}>Open messages</a> ·{' '}
      <a href="/account/orders?uiVersion=1">Open orders</a> ·{' '}
      <a href="/account/reviews?uiVersion=1">Open reviews</a></p>
  </div>;
}
